import React from "react";
import { View } from "react-native";

import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import type { ImageDto } from "$src/core/models/dto";
import isIPad from "$src/core/utils/device/is-ipad";
import getEpgChannelLogoUri from "$src/core/utils/epg/get-epg-channel-logo-uri";
import CustomPlatform from "$src/core/utils/platform/platform";

import $, { IMAGE_DIMENSIONS } from "./EPGChannelHeader.styles";

/** Only the phone identifies the channel through its paged selector alone. */
const SHOW_CHANNEL_HEADER = CustomPlatform.select({
  native: isIPad,
  default: true,
});

type EPGChannelHeaderProps = {
  title: string;
  logo?: ImageDto;
  logoDark?: ImageDto;
  isSelected?: boolean;
};

const EPGChannelHeader = ({
  title,
  logo,
  logoDark,
  isSelected,
}: EPGChannelHeaderProps) => {
  $.useVariants({ selected: !!isSelected });

  // Colour logo when selected, greyscale logoDark when idle (KAHI-268).
  const logoUri = getEpgChannelLogoUri({
    logo,
    logoDark,
    isActive: !!isSelected,
  });

  const logoSource = useResponsiveImage(logoUri, {
    dimensions: IMAGE_DIMENSIONS.logo,
  });

  if (!SHOW_CHANNEL_HEADER) {
    return null;
  }

  return (
    <View
      accessibilityLabel={title}
      accessibilityRole="header"
      style={$.channelHeader}
    >
      <Image
        accessibilityIgnoresInvertColors
        accessibilityLabel={title}
        accessibilityRole="image"
        accessible={false}
        contentFit="contain"
        source={logoSource}
        style={$.channelHeaderLogo}
      />
      <Text
        accessibilityRole="text"
        ellipsizeMode="tail"
        numberOfLines={1}
        preset="heading7"
        style={$.channelHeaderTitle}
      >
        {title}
      </Text>
    </View>
  );
};

export default EPGChannelHeader;
